import React from 'react';
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import CheckIcon from '@mui/icons-material/Check';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import { VocabularyTestBlock, VocabularyTestWord } from 'app/types';
import {
  DESKTOP_LAYOUT_BREAKPOINT,
  getVocabularyTestTopicKey,
} from 'pages/VocabularyTest/utils';

// A word counts toward the done/total nav counters when it carries any status
// — either the marking the user applied this session (`status`) or the status
// it already has in the user's Vocabulary (`existingStatus`). Mirrors the
// chip highlight logic in TestWord.tsx, so "counted" == "highlighted".
const isMarked = (word: VocabularyTestWord): boolean =>
  !!(word.status ?? word.existingStatus);

interface ICategoryNavProps {
  activeKey: string | null;
  categories: VocabularyTestBlock[];
  onJump: (key: string) => void;
  stickyTop: number;
}

interface IRailStripProps {
  onClick: () => void;
  stickyTop: number;
}

/**
 * Slim vertical strip shown in place of the collapsed category rail —
 * click to bring the full list back.
 */
export const RailStrip: React.FC<IRailStripProps> = ({
  onClick,
  stickyTop,
}) => (
  <IconButton
    onClick={onClick}
    sx={(theme) => ({
      alignSelf: 'flex-start',
      border: '1px solid',
      borderColor: 'primary.tableBorder',
      borderRadius: 2,
      display: 'none',
      flex: 'none',
      position: 'sticky',
      top: stickyTop,
      [theme.breakpoints.up(DESKTOP_LAYOUT_BREAKPOINT)]: {
        display: 'inline-flex',
      },
    })}
    title={'Показати категорії'}
  >
    <MenuOpenIcon />
  </IconButton>
);

/**
 * Shows only the currently loaded batch of categories (≤5 — one server
 * page of the test) so every row can carry a real, live done/total count.
 * There is no "jump to an arbitrary category" endpoint, so categories
 * outside this batch simply aren't listed here — StageNav's prev/next
 * is how the user reaches them.
 */
const CategoryNav: React.FC<ICategoryNavProps & { onCollapse: () => void }> = ({
  activeKey,
  categories,
  onJump,
  onCollapse,
  stickyTop,
}) => {
  const doneCount = categories.filter((category) =>
    category.words.every(isMarked),
  ).length;

  return (
    <Box
      component={'nav'}
      sx={(theme) => ({
        alignSelf: 'flex-start',
        display: 'none',
        flex: 'none',
        maxHeight: `calc(100vh - ${stickyTop + 16}px)`,
        overflowY: 'auto',
        position: 'sticky',
        top: stickyTop,
        width: 236,
        [theme.breakpoints.up(DESKTOP_LAYOUT_BREAKPOINT)]: {
          display: 'block',
        },
      })}
    >
      <Stack
        alignItems={'center'}
        direction={'row'}
        sx={{ pb: 1.25, pl: 1.5, pr: 0.75 }}
      >
        <Typography
          sx={{
            color: 'text.secondary',
            flex: 1,
            fontSize: 12,
            fontWeight: 800,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
          }}
        >
          {`Категорії · ${doneCount}/${categories.length}`}
        </Typography>
        <IconButton onClick={onCollapse} size={'small'} title={'Згорнути категорії'}>
          <ChevronLeftIcon fontSize={'small'} />
        </IconButton>
      </Stack>

      <Stack gap={0.25}>
        {categories.map((category) => {
          const key = getVocabularyTestTopicKey(category);
          const total = category.words.length;
          const done = category.words.filter(isMarked).length;
          const complete = total > 0 && done === total;
          const active = key === activeKey;
          const label = `${category.categoryName}. ${category.subCategoryName}`;

          return (
            <Tooltip key={key} placement={'right'} title={label}>
              <Box
                component={'button'}
                onClick={() => onJump(key)}
                sx={(theme) => ({
                  alignItems: 'center',
                  backgroundColor: active
                    ? alpha(theme.palette.primary.main, 0.12)
                    : 'transparent',
                  border: 'none',
                  borderRadius: 1,
                  cursor: 'pointer',
                  display: 'flex',
                  font: 'inherit',
                  gap: 1.25,
                  px: 1.5,
                  py: 1,
                  textAlign: 'left',
                  width: '100%',
                  '&:hover': {
                    backgroundColor: active
                      ? alpha(theme.palette.primary.main, 0.18)
                      : alpha(theme.palette.primary.main, 0.08),
                  },
                })}
              >
                <Box
                  sx={{
                    alignItems: 'center',
                    bgcolor: complete
                      ? '#3E9B5F'
                      : alpha('#3B2C93', 0.14),
                    borderRadius: '50%',
                    color: complete ? 'common.white' : 'primary.main',
                    display: 'inline-flex',
                    flex: 'none',
                    fontSize: 11,
                    fontWeight: 800,
                    height: 20,
                    justifyContent: 'center',
                    width: 20,
                  }}
                >
                  {complete ? <CheckIcon sx={{ fontSize: 14 }} /> : done || ''}
                </Box>
                <Typography
                  noWrap
                  sx={{
                    color: active ? 'primary.main' : 'text.primary',
                    flex: 1,
                    fontSize: 14,
                    fontWeight: active ? 700 : 500,
                    minWidth: 0,
                  }}
                >
                  {category.subCategoryName}
                </Typography>
                <Typography
                  sx={{ color: 'text.secondary', flex: 'none', fontSize: 11 }}
                >
                  {`${done}/${total}`}
                </Typography>
              </Box>
            </Tooltip>
          );
        })}
      </Stack>
    </Box>
  );
};

export default CategoryNav;
